import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useFlashDetail } from "@/hooks/useFlashDetail"
import { useArtists } from "@/hooks/useArtists"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useAuthState } from "@/hooks/useAuth"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Loading } from "@/components/ui/loading"
import { ArrowLeft } from "lucide-react"
import { FlashComments } from "@/components/comments/FlashComments"
import { CraveButton } from "@/components/fotw/CraveButton"
import { getUserVotesForWeek } from "@/lib/voteData"
import { useDynamicMetaTags } from "@/hooks/useDynamicMetaTags"
import { getCurrentWeekIndex } from "@/lib/utils"

export default function FotwFlashDetailPage() {
  const { flashId } = useParams<{ flashId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { flash, loading, error } = useFlashDetail(flashId)
  const { artists } = useArtists(flash ? [flash.artistId] : [])
  const { trackEvent } = useAnalytics()
  const { user } = useAuthState()
  const [hasVoted, setHasVoted] = useState(false)

  const artist = artists[0]
  const currentWeekIndex = getCurrentWeekIndex()

  // Update meta tags for social media sharing  
  useDynamicMetaTags({
    title: flash && artist ? `${artist.displayName} - Flash of the Week` : undefined,
    description: flash ? `Vote for this Flash of the Week!${flash.minPrice ? ` Starting from ${flash.minPrice}€` : ''}${flash.minSize ? `, size ${flash.minSize}` : ''}` : undefined,
    image: flash?.imageUrl,
    url: flash ? `https://getmoretats.com/fotw/${flash.id}` : undefined,
    type: 'article'
  })

  // Check if user has voted this week
  useEffect(() => {
    const checkUserVotes = async () => {
      if (user && flash?.fotw) {
        const votes = await getUserVotesForWeek(user.uid, currentWeekIndex)
        console.log('Detail page votes check:', { userId: user.uid, currentWeekIndex, votes, hasVoted: votes.length > 0 })
        setHasVoted(votes.length > 0)
      } else {
        setHasVoted(false)
      }
    }
    checkUserVotes()
  }, [user, currentWeekIndex, flash?.fotw, user?.uid, flash?.votes]) // Added flash.votes as dependency
  
  // Also check votes when the page becomes visible
  useEffect(() => {
    const checkUserVotes = async () => {
      if (user && flash?.fotw) {
        const votes = await getUserVotesForWeek(user.uid, currentWeekIndex)
        console.log('Detail page visibility check:', { userId: user.uid, currentWeekIndex, votes, hasVoted: votes.length > 0 })
        setHasVoted(votes.length > 0)
      }
    }
    
    // Run immediately when component mounts/becomes visible
    checkUserVotes()
    
    // Also add a visibility change listener
    const handleVisibilityChange = () => {
      if (!document.hidden && user && flash?.fotw) {
        checkUserVotes()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [user, currentWeekIndex, flash?.fotw])

  useEffect(() => {
    if (flash && artist) {
      trackEvent('item_viewed', {
        item_id: flash.id,
        item_type: 'flash',
        artist_id: flash.artistId,
        source: 'fotw_detail'
      })
    }
  }, [flash, artist, trackEvent])

  const handleShowArtist = () => {
    if (artist) {
      navigate(`/artist/${artist.id}`)
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Loading />
      </div>
    )
  }

  if (error || !flash) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-4">{t('common.flashNotFound')}</p>
          <Button onClick={handleBack} variant="outline">
            {t('common.goBack')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="phone-container-padded py-4">
          {/* Header - simple back button with text */}
          <div className="flex items-center gap-2 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </Button>
          </div>

          {/* Flash Image */}
          <div className="mb-6 flex justify-center">
            <div className="relative w-3/4 rounded-[var(--card-radius)] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
              <img
                src={flash.imageUrl}
                alt={flash.caption || "Flash tattoo design"}
                className="w-full h-auto object-cover"
              />
              
              {/* Artist Avatar - Top Left */}
              {artist && (
                <div 
                  className="absolute top-2 left-2 w-12 h-12 rounded-[var(--card-radius)] overflow-hidden bg-muted cursor-pointer shadow-lg"
                  onClick={handleShowArtist}
                >
                  {artist.avatarUrl ? (
                    <img
                      src={artist.avatarUrl}
                      alt={artist.displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-sm font-bold text-muted-foreground">
                        {artist.displayName.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Size pill - Bottom Left */}
              {flash.minSize && (
                <div className="absolute bottom-2 left-2 bg-background text-foreground text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                  <span>~{flash.minSize}cm</span>
                </div>
              )}
              
              {/* Price pill - Bottom Right */}
              {flash.minPrice && (
                <div className="absolute bottom-2 right-2 bg-background text-foreground text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                  <span>{flash.minPrice}€</span>
                </div>
              )}

              {/* Vote count pill - Bottom Center */}
              {((flash.timedVotes && flash.timedVotes.length > 0) || (flash.votes && flash.votes.length > 0)) && (
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-muted text-muted-foreground text-xs font-medium px-2 py-1 rounded-full">
                  {(user && hasVoted) ? (
                    `${flash.timedVotes?.length || flash.votes?.length || 0} 🙏`
                  ) : (
                    <>
                      <span className="select-none" style={{ filter: 'blur(3px)' }}>{flash.timedVotes?.length || flash.votes?.length || 0}</span> 🙏
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-6">
            {/* Crave Button - Primary voting action */}
            {flash.fotw && (
              <div className="flex-1">
                <CraveButton item={flash} weekIndex={currentWeekIndex} />
              </div>
            )}
          </div>

          {/* Comments Section */}
          <FlashComments flashId={flash.id} />
        </div>
      </div>
    </>
  )
}
